import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { makeAutoObservable, computed, action } from 'mobx';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import {
    checkIsArray,
    divideBy100,
    getFieldsFromCriteria,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import {
    isMultiselectNotEmpty,
    isNumeric,
    isNumericBetween,
    isNumericRelationWithInput,
    isNumericRelationWithNumber,
    validateEmptyIf,
    validateIf,
    validateNotEmptyIf,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';
import { SportsPickerState } from './sports-picker/SportsPickerState.state';
import { CampaignCreatorState } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';
import { Common } from 'src/AppState/Common';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';

export type SportsbookSelectionsFormType = {
    'bet-type': string;
    'bet-min-odds': string;
    stakeRequirements: string;
    'bet-result': string;
};

export type SportsbookSelectionsFormSingleType = {
    'min-stake': string;
    'max-stake': string;
};

export type SportsbookSelectionsFormCumulativeType = {
    'target-stake': string;
    'min-stake': string;
    'min-bet-frequency': string;
};

interface StakeRequirementsType {
    value: 'single' | 'cumulative' | '';
    label: string;
    dataTest?: string;
}

const allStakeRequirementsOption: Array<StakeRequirementsType> = [
    { dataTest: 'single', value: 'single', label: 'Single' },
    { dataTest: 'cumulative', value: 'cumulative', label: 'Cumulative' },
];

export class SportsbookSelectionsState {
    public readonly betTypeForm: FormInputState<string, string>;
    public readonly minBetOddsForm: FormInputState<string, string>;
    public readonly stakeRequirementsForm: FormInputState<StakeRequirementsType['value'], string>;

    public readonly betResultForm: FormInputState<string, string>;

    public readonly form: FormModel<SportsbookSelectionsFormType>;
    public readonly targetStakeForm: FormInputState<string, string>;
    // cumulative stake
    public readonly formCumulative: FormModel<SportsbookSelectionsFormCumulativeType>;
    public readonly minStakeCumulativeForm: FormInputState<string, string>;
    public readonly minBetFrequencyForm: FormInputState<string, string>;
    // single stake
    public readonly formSingle: FormModel<SportsbookSelectionsFormSingleType>;
    public readonly minStakeSingleForm: FormInputState<string, string>;
    public readonly maxStakeSingleForm: FormInputState<string, string>;

    public readonly sportsPickerState: SportsPickerState;

    private readonly noEmptyValidationMessage = 'Required field';

    public constructor(
        public readonly common: Common,
        public readonly campaignId: string | undefined
    ) {
        makeAutoObservable(this);
        this.sportsPickerState = new SportsPickerState($appState.common);

        this.betTypeForm = FormInputState.new('').map(isMultiselectNotEmpty(false, this.noEmptyValidationMessage));
        this.minBetOddsForm = FormInputState.new('')
            .map(validateNotEmpty(this.noEmptyValidationMessage))
            .map(isNumericRelationWithNumber(1, 'more', true, () => true, 'Enter a value greater or equal to 1'));
        this.stakeRequirementsForm = FormInputState.new<'cumulative' | 'single' | ''>('').map(
            validateNotEmpty(this.noEmptyValidationMessage)
        );

        this.betResultForm = FormInputState.new('').map((value) => {
            return validateIf(
                () => {
                    const campaignState = CampaignCreatorState.get(this.common, this.campaignId);
                    return campaignState.generalDetailsState.campaignTypeForm.value === 'settlement';
                },
                () => isMultiselectNotEmpty(false, this.noEmptyValidationMessage)(value)
            );
        });

        this.form = FormModel.group({
            'bet-type': this.betTypeForm,
            'bet-min-odds': this.minBetOddsForm,
            stakeRequirements: this.stakeRequirementsForm,
            'bet-result': this.betResultForm,
        });

        // cumulative stake
        this.targetStakeForm = FormInputState.new('')
            .map(validateNotEmptyIf(this.noEmptyValidationMessage, () => this.stakeRequirements === 'cumulative'))
            .map(
                isNumericRelationWithNumber(
                    0.1,
                    'more',
                    true,
                    () => this.stakeRequirements === 'cumulative',
                    'Enter a value greater or equal to £0.10'
                )
            );

        this.minStakeCumulativeForm = FormInputState.new('').map(
            isNumericRelationWithNumber(0.1, 'more', true, () => false, 'Enter a value greater or equal to £0.10')
        );

        this.minBetFrequencyForm = FormInputState.new('').map(isNumericBetween({ from: 1, to: 100, optional: true }));
        this.formCumulative = FormModel.group({
            'target-stake': this.targetStakeForm,
            'min-stake': this.minStakeCumulativeForm,
            'min-bet-frequency': this.minBetFrequencyForm,
        });
        // single stake
        this.minStakeSingleForm = FormInputState.new('')
            .map(validateNotEmptyIf(this.noEmptyValidationMessage, () => this.stakeRequirements === 'single'))
            .map(
                isNumericRelationWithNumber(
                    0.1,
                    'more',
                    true,
                    () => this.stakeRequirements === 'single',
                    'Enter a value greater or equal to £0.10'
                )
            );

        this.maxStakeSingleForm = FormInputState.new('')
            .map(validateEmptyIf('Enter `Min Stake` value first', () => this.minStakeSingleForm.value === ''))
            .map(isNumeric(true, 'Enter a value greater than ‘Min Stake’'))
            .map(
                isNumericRelationWithInput(
                    () => this.minStakeSingleForm,
                    'more',
                    false,
                    true,
                    'Enter a value greater than ‘Min Stake’'
                )
            );
        this.formSingle = FormModel.group({
            'min-stake': this.minStakeSingleForm,
            'max-stake': this.maxStakeSingleForm,
        });
    }

    @action public validateForm(): void {
        this.form.setAsVisited();
        this.formCumulative.setAsVisited();
        this.formSingle.setAsVisited();
    }

    @computed public get isFormValid(): boolean {
        return (
            this.form.result.value.type === 'ok' &&
            this.formCumulative.result.value.type === 'ok' &&
            this.formSingle.result.value.type === 'ok'
        );
    }

    @computed public get formErrorsCount(): number {
        let errorsCount = 0;

        errorsCount += this.form.errorsForView.length;
        errorsCount += this.formCumulative.errorsForView.length;
        errorsCount += this.formSingle.errorsForView.length;

        return errorsCount;
    }

    public isTouched(): boolean {
        return (
            this.form.isTouched() ||
            this.formCumulative.isTouched() ||
            this.formSingle.isTouched() ||
            this.sportsPickerState.isTouched()
        );
    }

    public setIsTouched(): void {
        this.form.setIsTouched();
        this.formCumulative.setIsTouched();
        this.formSingle.setIsTouched();
    }

    public resetIsTouched(): void {
        this.form.resetIsTouched();
        this.formCumulative.resetIsTouched();
        this.formSingle.resetIsTouched();
        this.sportsPickerState.resetIsTouched();
    }

    public resetIsVisited(): void {
        this.form.resetIsVisited();
        this.formCumulative.resetIsVisited();
        this.formSingle.resetIsVisited();
    }

    public get allStakeRequirementsOptions(): Array<StakeRequirementsType> {
        return allStakeRequirementsOption;
    }

    public get stakeRequirements(): StakeRequirementsType['value'] {
        return this.stakeRequirementsForm.value;
    }

    public formatNumericInput = (formInput: FormInputState<string, string>): void => {
        const value = formInput.value.trim();

        if (value === '') {
            formInput.setAsVisited();
            return;
        }

        const numericValue = parseFloat(value);

        if (isNaN(numericValue)) {
            formInput.setAsVisited();
            return;
        }

        const formattedValue = numericValue.toFixed(2);
        formInput.setValue(formattedValue);
        formInput.setAsVisited();
    };

    @action public setFormValues = async (campaignModel: CampaignModel): Promise<void> => {
        const transformedCriteria = getFieldsFromCriteria(campaignModel);

        await this.sportsPickerState.setInitValues({
            sportIds: checkIsArray(transformedCriteria?.['sport']) ?? [],
            competitionIds: checkIsArray(transformedCriteria?.['competition']) ?? [],
            eventIds: checkIsArray(transformedCriteria?.['event']) ?? [],
            marketIds: checkIsArray(transformedCriteria?.['market']) ?? [],
        });

        const minStakeValue = divideBy100(transformedCriteria?.['min-stake'] ?? '');
        const maxStakeValue = divideBy100(transformedCriteria?.['max-stake'] ?? '');
        const minBetOddsValue = transformedCriteria?.['bet-min-odds'] ?? '';
        const targetStake = divideBy100(transformedCriteria?.['target-stake'] ?? '');
        const minBetFrequency = transformedCriteria?.['min-bet-frequency'] ?? '';
        const betResult = transformedCriteria?.['bet-result'] ?? '';
        const betType = transformedCriteria?.['bet-type'] ?? '';

        const isCumulative = targetStake !== '';

        if (isCumulative) {
            this.minStakeCumulativeForm.setValue(minStakeValue);
        } else {
            this.minStakeSingleForm.setValue(minStakeValue);
        }

        this.maxStakeSingleForm.setValue(maxStakeValue);
        this.minBetOddsForm.setValue(minBetOddsValue);
        this.minBetFrequencyForm.setValue(minBetFrequency);
        this.betTypeForm.setValue(betType);
        this.stakeRequirementsForm.setValue(isCumulative ? 'cumulative' : 'single');
        this.targetStakeForm.setValue(targetStake);
        this.betResultForm.setValue(betResult);
    };
}
